import { useEffect, useState } from "react";
import VariantCard from "./VariantCard";
import SingleVariantPanel from "./SingleVariantPanel";

export default function VariantsSection({ form }) {
  const {
    formData,
    setFormData,
    validationErrors,
    groupColorsByVariant,
    addVariant,
    clearFieldErrors,
  } = form;

  const groupedVariants = groupColorsByVariant(formData.colors || []);
  const variantNames = Object.keys(groupedVariants);
  const variantCount = variantNames.length;

  const [expandedIndex, setExpandedIndex] = useState(0);

  useEffect(() => {
    if (formData.singleVarinet) return;
    if (variantNames.length === 0) {
      setExpandedIndex(-1);
      return;
    }
    // Allow -1 (all collapsed). Only fix index if a variant was removed.
    if (expandedIndex >= variantNames.length) {
      setExpandedIndex(variantNames.length - 1);
    }
  }, [formData.singleVarinet, variantNames.length, expandedIndex]);

  const setMode = (single) => {
    setFormData((prev) => ({
      ...prev,
      singleVarinet: single,
    }));
    clearFieldErrors("colors");
  };

  const allCollapsed = expandedIndex < 0;

  return (
    <section data-field="variants" className="pf-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h3 className="pf-section-title">Variant Management</h3>
        <div className="flex flex-wrap items-center gap-2">
          <div className="pf-mode-toggle">
            <button
              type="button"
              className={!formData.singleVarinet ? "active" : ""}
              onClick={() => setMode(false)}
            >
              Multi-Variant
            </button>
            <button
              type="button"
              className={formData.singleVarinet ? "active" : ""}
              onClick={() => setMode(true)}
            >
              Single-Variant
            </button>
          </div>
        </div>
      </div>

      {validationErrors.variants && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4 text-sm">
          {validationErrors.variants}
        </div>
      )}

      {formData.singleVarinet ? (
        <SingleVariantPanel form={form} />
      ) : (
        <>
          {variantNames.length === 0 ? (
            <div className="rounded border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center mb-3">
              <p className="text-sm font-semibold text-gray-800 mb-1">
                No variants yet
              </p>
              <p className="text-xs text-gray-500 mb-4">
                Add a size/pack variant, then configure price, gallery, and
                colors.
              </p>
              <button type="button" onClick={addVariant} className="pf-dashed-btn">
                + Add New Product Variant
              </button>
            </div>
          ) : (
            <div className="space-y-2.5">
              {variantNames.map((variantName, variantGroupIndex) => (
                <VariantCard
                  key={
                    groupedVariants[variantName]?.[0]?.originalIndex ??
                    variantGroupIndex
                  }
                  form={form}
                  variantName={variantName}
                  variantGroupIndex={variantGroupIndex}
                  colorsInVariant={groupedVariants[variantName]}
                  variantNamesLength={variantNames.length}
                  expanded={expandedIndex === variantGroupIndex}
                  onToggle={() =>
                    setExpandedIndex((prev) =>
                      prev === variantGroupIndex ? -1 : variantGroupIndex,
                    )
                  }
                />
              ))}
            </div>
          )}

          {variantCount > 0 && variantCount < 9999 && (
            <button
              type="button"
              onClick={() => {
                addVariant();
                setExpandedIndex(variantCount);
              }}
              className="pf-dashed-btn mt-3"
            >
              + Add New Product Variant
            </button>
          )}
        </>
      )}
    </section>
  );
}
